import { Color, Spacing } from '@persona-id/design-tokens';
import { CaretDownIcon, MoreIcon } from '@persona-id/design-tokens/icons';
import * as SelectPrimitive from '@radix-ui/react-select';
import { Property } from 'csstype';
import { forwardRef, ReactNode } from 'react';
import styled, { css } from 'styled-components';

import { Z_INDICES } from 'dashboard/styles/variables';
import { IconButton } from 'design-system/components/Button';
import DropdownMenu from 'design-system/components/DropdownMenu';
import useFormField from 'design-system/components/FormControl/useFormField';
import Menu, { MenuItem, MenuItemProps, MenuList, MenuProps } from 'design-system/components/Menu';
import { HStack } from 'design-system/components/Stack';
import { Paragraph } from 'design-system/components/Text';
import { isNotDisabled } from 'design-system/lib/pseudoClass';
import inputMixin, { InputMixinProps, InputSizes } from 'design-system/mixins/input';
import { paragraphMixins } from 'design-system/mixins/text';

export interface SelectProps extends InputMixinProps, Omit<SelectPrimitive.SelectProps, 'value'> {
  'aria-label'?: string;
  placeholder?: string;

  /**
   * id will be applied to trigger
   */
  id?: string;

  /**
   * Class name will be applied to trigger
   */
  className?: string;

  /**
   * Max height of options menu
   */
  maxHeight?: number;

  /**
   * Min width of the select
   */
  minWidth?: number;

  /**
   * Width of the selected value container
   */
  valueContainerWidth?: 'auto' | '100%';

  /**
   * Render dropdown menu in a portal.
   */
  portal?: boolean;

  /**
   * Props to apply to Radix Select.Trigger within
   */
  triggerProps?: SelectPrimitive.SelectTriggerProps;

  /**
   * Props to apply to Radix Select.Content within
   */
  contentProps?: SelectPrimitive.SelectContentProps;

  /**
   * Callback meant for use with `react-hook-form`'s `useForm` (or Persona's `useFormSecurely`)
   */
  onChange?: (event: { target: { value: string } }) => void;

  size?: InputSizes;

  value?: string | null;

  width?: string;

  header?: ReactNode;

  /**
   * z-index of content. Please use sparingly.
   */
  zIndex?: Property.ZIndex;
}

interface SelectButtonProps extends InputMixinProps {
  isSelectingPlaceholder: boolean;
  $width?: string;
  $minWidth?: number;
}

const SelectButton = styled('button')<SelectButtonProps>`
  ${inputMixin};
  display: inline-flex;
  position: relative;
  align-items: center;
  outline: none;
  appearance: none;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
  padding-right: ${Spacing[6]};
  width: ${({ $width }) => $width ?? '100%'};
  min-width: ${({ $minWidth }) => ($minWidth != null ? `${$minWidth}px` : 'auto')};
  pointer-events: auto;

  &${isNotDisabled} {
    cursor: pointer;
  }

  ${({ theme, isSelectingPlaceholder }) =>
    isSelectingPlaceholder &&
    css`
      color: ${theme.color.text.secondary};
    `}
`;

interface SelectValueContainerProps {
  width?: string;
}

const SelectValueContainer = styled.span<SelectValueContainerProps>`
  text-overflow: ellipsis;
  min-width: 0px;
  overflow: hidden;
  text-align: start;

  ${({ width }) =>
    width &&
    css`
      width: ${width};
    `}
`;

const OptionSubtitle = styled.div`
  color: ${({ theme }) => theme.color.text.secondary};
  font-size: ${({ theme }) => theme.text.paragraph.sm.fontSize};
  margin-top: ${Spacing[1]};
`;

interface OptionsMenuProps {
  maxHeight?: number;
  size?: InputSizes;
}

const OptionsMenu = styled(Menu)<OptionsMenuProps>`
  width: var(--radix-select-trigger-width);
  max-height: ${({ maxHeight }) => maxHeight ?? '225px !important'};
  overflow-y: auto;
  margin-bottom: 0;

  ${({ size }) => {
    switch (size) {
      case 'md': {
        return css`
          ${MenuList} {
            padding: ${Spacing[1]};
          }

          ${OptionItem} {
            ${paragraphMixins.md};
            padding: ${Spacing[2]};
          }
        `;
      }
      case 'sm': {
        return css`
          ${MenuList} {
            padding: ${Spacing[1]};
          }

          ${OptionItem} {
            ${paragraphMixins.sm};
            padding: ${Spacing[1]};
          }
        `;
      }
    }
  }}
`;

const SelectContent = styled(SelectPrimitive.Content)<{ $zIndex?: number | string }>`
  z-index: ${({ $zIndex }) => $zIndex || Z_INDICES.dropdown};
`;

const SelectHeader = styled(HStack)`
  display: flex;
  justify-content: space-between;
  padding: ${Spacing[1]} ${Spacing[1]} 0 ${Spacing[1]};
  margin-bottom: ${Spacing[1]};
`;

const SelectMenu = forwardRef<HTMLUListElement, MenuProps & OptionsMenuProps>(function SelectMenu(
  props: MenuProps,
  forwardedRef,
) {
  return <OptionsMenu listRef={forwardedRef} {...props} />;
});

const OptionItem = styled(MenuItem)`
  ${paragraphMixins.md};
  padding: ${Spacing[4]};
`;

const SelectItem = forwardRef<HTMLDivElement, MenuItemProps>(function SelectItem(
  props: MenuItemProps,
  forwardedRef,
) {
  return <OptionItem ref={forwardedRef} {...props} />;
});

const StyledIconContainer = styled(SelectPrimitive.Icon)<{ disabled?: boolean }>`
  position: absolute;
  right: ${Spacing[2]};
  display: flex;
  justify-content: center;

  ${({ disabled }) => css`
    color: ${disabled ? Color.BorderIconDisabled : Color.BorderIconPrimary};
  `}
`;

interface HeaderProps {
  title?: ReactNode;
  menuItems?: ReactNode;
  menuTriggerLabel?: string;
}

export const Header = ({ title, menuItems, menuTriggerLabel }: HeaderProps) => {
  return (
    <SelectHeader>
      {title && (
        <Paragraph color='primary' weight='bold' size='md'>
          {title}
        </Paragraph>
      )}
      {menuItems && (
        <DropdownMenu
          trigger={
            <IconButton>
              <MoreIcon />
            </IconButton>
          }
          triggerProps={{ 'aria-label': menuTriggerLabel }}
          side='right'
          align='start'
          zIndex='auto'
          portal
        >
          {menuItems}
        </DropdownMenu>
      )}
    </SelectHeader>
  );
};

const Select = forwardRef<HTMLButtonElement, SelectProps>(function Select(
  { portal = false, size = 'md', ...props }: SelectProps,
  forwardedRef,
) {
  const {
    'aria-label': ariaLabel,
    hasError,
    hasWarning,
    focused,
    active,
    hovered,
    disabled,
    placeholder,
    maxHeight,
    children,
    triggerProps,
    contentProps,
    id,
    className,
    onChange,
    onValueChange,
    value,
    width,
    header,
    minWidth,
    valueContainerWidth,
    zIndex,
  } = props;

  const field = useFormField({ inputId: props.id, disabled, hasWarning, hasError });

  const content = (
    <SelectContent
      position='popper'
      side='bottom'
      sideOffset={8}
      $zIndex={zIndex}
      {...contentProps}
    >
      <SelectMenu maxHeight={maxHeight} size={size}>
        {header}
        <SelectPrimitive.Viewport>{children}</SelectPrimitive.Viewport>
      </SelectMenu>
    </SelectContent>
  );

  return (
    <SelectPrimitive.Root
      {...props}
      onValueChange={(value) => {
        onChange?.({ target: { value } });
        onValueChange?.(value);
      }}
      // Custom behavior to consider null as empty value
      value={value === null ? '' : value} // eslint-disable-line @persona-id/eqeq-null-undefined
    >
      <SelectPrimitive.Trigger
        ref={forwardedRef}
        asChild
        id={id}
        className={className}
        disabled={field.disabled}
        aria-describedby={field.helperTextId}
        aria-errormessage={field.hasError ? field.helperTextId : undefined}
        aria-invalid={field.hasError}
        aria-label={ariaLabel}
        {...triggerProps}
      >
        <SelectButton
          hasWarning={field.hasWarning}
          hasError={field.hasError}
          hovered={hovered}
          focused={focused}
          active={active}
          disabled={field.disabled}
          placeholder={placeholder}
          isSelectingPlaceholder={props.value === '' || props.value == null}
          $minWidth={minWidth}
          $size={size}
          $width={width}
        >
          <SelectValueContainer width={valueContainerWidth}>
            <SelectPrimitive.Value placeholder={placeholder} />
          </SelectValueContainer>
          <StyledIconContainer disabled={disabled}>
            <CaretDownIcon />
          </StyledIconContainer>
        </SelectButton>
      </SelectPrimitive.Trigger>
      {portal ? <SelectPrimitive.Portal>{content}</SelectPrimitive.Portal> : content}
    </SelectPrimitive.Root>
  );
});

export default Select;

export const Option = forwardRef<HTMLDivElement, SelectPrimitive.SelectItemProps & MenuItemProps>(
  function Option({ children, subtitle, ...props }, forwardedRef) {
    return (
      <SelectPrimitive.Item {...props} ref={forwardedRef} asChild>
        <SelectItem {...props}>
          <SelectPrimitive.ItemText>{children}</SelectPrimitive.ItemText>
          {subtitle && <OptionSubtitle>{subtitle}</OptionSubtitle>}
        </SelectItem>
      </SelectPrimitive.Item>
    );
  },
);
